/**
 * ProjectSwitcher — top-bar dropdown to switch between projects.
 *
 * Features:
 *   - Shows current project name (from URL) or placeholder text.
 *   - Click opens dropdown with: search input, list of recent 8 projects,
 *     "Ver todos" link to /projects.
 *   - localStorage tracks recently visited project IDs (last 8).
 *   - Search filters list by name.
 *   - Clicking a project navigates, preserving sub-route when applicable:
 *       /projects/A/orcamento → /projects/B/orcamento
 *       non-project route → /projects/B
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useAuth } from '@/store/auth.store';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';
import type { Project } from '@builder/shared';

/**
 * Extract the project id from a pathname like /projects/<id>/...
 * Returns null when the pathname is not a project-scoped route.
 */
export function extractProjectIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/projects\/([^/]+)/);
  return match ? (match[1] ?? null) : null;
}

// ── localStorage helpers ────────────────────────────────────────────────────

const LS_KEY = 'project_switcher_recent';
const MAX_RECENT = 8;

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveRecent(ids: string[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(ids.slice(0, MAX_RECENT)));
  } catch {
    // storage full / unavailable — silent
  }
}

export function pushRecentProject(id: string) {
  const prev = loadRecent().filter((x) => x !== id);
  saveRecent([id, ...prev]);
}

// ── Sub-route navigation helper ─────────────────────────────────────────────

/**
 * Given the current pathname and a target project id, compute the destination.
 * If pathname is /projects/<currentId>/rest, returns /projects/<targetId>/rest.
 * Otherwise returns /projects/<targetId>.
 */
export function buildProjectPath(pathname: string, targetId: string): string {
  const match = pathname.match(/^\/projects\/([^/]+)(\/.*)?$/);
  if (match) {
    const rest = match[2] ?? '';
    return `/projects/${targetId}${rest}`;
  }
  return `/projects/${targetId}`;
}

// ── Component ────────────────────────────────────────────────────────────────

export function ProjectSwitcher() {
  const { t } = useT();
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [recentIds, setRecentIds] = useState<string[]>(() => loadRecent());

  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  // Fetch the full project list — only when authenticated.
  const { data: projects } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: () => api.listProjects(),
    enabled: !!user,
    staleTime: 60_000,
  });

  // Keep recentIds in sync with localStorage on mount.
  useEffect(() => {
    setRecentIds(loadRecent());
  }, [open]);

  // Close dropdown when clicking outside or pressing Escape.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Focus search when dropdown opens.
  useEffect(() => {
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch('');
    }
  }, [open]);

  // Build the sorted list of projects to show:
  // recent ones first (up to 8), rest appended; search filters on top.
  const sortedProjects = useCallback((): Project[] => {
    if (!projects) return [];
    const recentOrdered = recentIds
      .map((id) => projects.find((p) => p.id === id))
      .filter((p): p is Project => !!p);
    const rest = projects.filter((p) => !recentIds.includes(p.id));
    return [...recentOrdered, ...rest].slice(0, MAX_RECENT);
  }, [projects, recentIds]);

  const visibleProjects = (() => {
    const list = sortedProjects();
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((p) => p.name.toLowerCase().includes(q));
  })();

  // The "current" project is derived from the current pathname.
  const currentId = extractProjectIdFromPath(location.pathname);
  const currentProject = projects?.find((p) => p.id === currentId);
  const currentName = currentProject?.name ?? null;

  // Don't render if not authenticated or no projects fetched yet.
  if (!user || !projects) return null;

  const handleSelect = (project: Project) => {
    // Record visit.
    const prev = loadRecent().filter((x) => x !== project.id);
    saveRecent([project.id, ...prev]);
    setRecentIds([project.id, ...prev]);

    setOpen(false);
    const dest = buildProjectPath(location.pathname, project.id);
    navigate(dest);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        data-testid="project-switcher-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t.projectSwitcher.triggerAriaLabel}
        className={cn(
          'pressable focus-ring flex min-h-[44px] items-center gap-1.5 rounded-full border border-line',
          'bg-surface-2 px-3 py-1.5 text-sm transition-colors duration-150',
          'hover:bg-surface-3 hover:text-ink',
          currentName ? 'text-ink' : 'text-ink-muted',
        )}
      >
        <svg
          className="h-3.5 w-3.5 shrink-0 text-ink-muted"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <rect x="2" y="2" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
          <rect x="11" y="2" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
          <rect x="2" y="11" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
          <rect x="11" y="11" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
        </svg>
        <span className="hidden sm:inline max-w-[120px] truncate">
          {currentName ?? t.projectSwitcher.placeholder}
        </span>
        <svg
          className={cn('h-3.5 w-3.5 shrink-0 transition-transform duration-150', open && 'rotate-180')}
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        // listen-to-JP-fix · 2026-05-20 — z-50 (was z-40) so the dropdown
        // outranks later TopBar siblings (InboxBell's "Nova" button, the
        // avatar menu, etc.) that previously bled through it. The header
        // shell itself is z-30 but each `relative` sibling in the right
        // cluster opens a new local stacking context, so without a high
        // enough z here the *next* element in DOM order paints on top.
        // Unified TopBar dropdown stack is z-50 (matches Avatar/Inbox/
        // Notifications now — see TopBar.tsx and the audit table).
        <div
          role="listbox"
          data-testid="project-switcher-dropdown"
          style={{ transformOrigin: 'top left' }}
          className={cn(
            'absolute left-0 mt-2 w-72 overflow-hidden rounded-xl border border-line z-50',
            'bg-surface-2 shadow-pop dark:shadow-pop-dark animate-scale-in',
          )}
        >
          {/* Search */}
          <div className="border-b border-line px-3 py-2">
            <div className="relative">
              <svg
                className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-muted"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
                <path d="M14 14l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                data-testid="project-switcher-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.projectSwitcher.searchPlaceholder}
                className={cn(
                  'w-full rounded-md border-0 bg-surface-3 py-1.5 pl-7 pr-3',
                  'text-sm text-ink placeholder:text-ink-muted',
                  'focus:outline-none focus:ring-1 focus:ring-accent',
                )}
              />
            </div>
          </div>

          {/* Project list */}
          <ul className="max-h-64 overflow-y-auto py-1" role="presentation">
            {visibleProjects.length === 0 ? (
              <li className="px-3 py-2 text-sm text-ink-muted">
                {t.projectSwitcher.noResults}
              </li>
            ) : (
              visibleProjects.map((project) => (
                <li key={project.id} role="none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={project.id === currentId}
                    data-testid={`project-option-${project.id}`}
                    onClick={() => handleSelect(project)}
                    className={cn(
                      'w-full min-h-[44px] px-3 py-2 text-left text-sm',
                      'flex items-center gap-2 transition-colors duration-100',
                      project.id === currentId
                        ? 'bg-surface-3 text-ink font-medium'
                        : 'text-ink hover:bg-surface-3',
                    )}
                  >
                    <span className="truncate flex-1">{project.name}</span>
                    {project.id === currentId && (
                      <svg
                        className="h-3.5 w-3.5 shrink-0 text-accent"
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path d="M4 10l5 5 7-8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>

          {/* Footer: Ver todos link */}
          <div className="border-t border-line px-3 py-2">
            <Link
              to="/projects"
              data-testid="project-switcher-view-all"
              onClick={() => setOpen(false)}
              className={cn(
                'flex min-h-[44px] items-center gap-1.5 text-sm text-ink-muted',
                'hover:text-ink transition-colors duration-100',
              )}
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t.projectSwitcher.viewAll}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
